import { useState, useCallback, useEffect } from 'react';
import { getEmptyFormData } from './constants';

// Хук для работы с picker справочника (аналогично Form4)
export const usePicker = (
  handbooks,
  formData,
  modalData,
  updateFormData,
  updateModalData,
  loadSignsForCause,
  previousCause,
  setPreviousCause
) => {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerField, setPickerField] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const [activeModalItemId, setActiveModalItemId] = useState(null);

  const openPicker = useCallback((fieldName, handbookId, itemId) => {
    const handbook = handbooks.find(h => h.id === handbookId);
    
    if (handbook && handbook.references) {
      const options = handbook.references.map(r => ({
        value: r.id.toString(),
        label: fieldName === 'prichina' && r.code ? `${r.name} (${r.code})` : r.name,
        description: r.description || null
      }));
      
      if (options && options.length > 0) {
        let currentValue = '';
        
        if (itemId) {
          // Для полей модального окна берем значение из конкретного элемента
          const item = modalData.find(m => m.id === itemId);
          currentValue = item ? item[fieldName] || '' : '';
          setActiveModalItemId(itemId);
        } else {
          // Для полей формы берем значение из formData
          currentValue = formData ? (formData[fieldName] || '') : '';
          setActiveModalItemId(null);
        }
        
        const selectedIndex = options.findIndex(opt => opt.value === currentValue);
        
        setPickerField(fieldName);
        setPickerOptions(options);
        setPickerSearch('');
        setSelectedOptionIndex(selectedIndex);
        setShowPicker(true);
      }
    }
  }, [handbooks, modalData, formData]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setPickerField('');
    setPickerOptions([]);
    setPickerSearch('');
    setSelectedOptionIndex(-1);
    setActiveModalItemId(null);
  }, []);

  const selectPickerValue = useCallback(async (value) => {
    const selectedOption = pickerOptions.find(opt => opt.value === value);
    const valueToSave = selectedOption ? selectedOption.value : value;
    
    if (activeModalItemId) {
      // Обновляем данные в модальном окне
      updateModalData(activeModalItemId, pickerField, valueToSave);
      
      // Если выбрана причина, загружаем признаки
      if (pickerField === 'prichina' && selectedOption) {
        // Проверяем, изменилась ли причина
        const currentCause = previousCause[activeModalItemId];
        if (currentCause !== selectedOption.label) {
          // Очищаем поле признака при смене причины
          updateModalData(activeModalItemId, 'prizn', '');
          // Обновляем предыдущую причину
          setPreviousCause(prev => ({
            ...prev,
            [activeModalItemId]: selectedOption.label
          }));
        }
        
        // Загружаем признаки для новой причины
        await loadSignsForCause(selectedOption.label);
      }
    } else {
      // Обновляем данные формы напрямую
      const currentFormData = formData || getEmptyFormData();
      const newFormData = { ...currentFormData, [pickerField]: valueToSave };
      
      // Если выбрана причина (ochagPrichina), проверяем и очищаем "Вид ВО" если нужно
      if (pickerField === 'ochagPrichina' && valueToSave) {
        // Получаем оригинальный элемент из справочника 10, чтобы получить sign_codes
        const handbook10 = handbooks.find(h => h.id === 10);
        if (handbook10 && handbook10.references) {
          const prichinaItem = handbook10.references.find(r => r.id.toString() === valueToSave);
          if (prichinaItem && prichinaItem.sign_codes) {
            const signCodes = Array.isArray(prichinaItem.sign_codes) ? prichinaItem.sign_codes : [];
            const currentVidVO = currentFormData.ochagVidVO || '';
            
            if (currentVidVO && signCodes.length > 0) {
              // Проверяем, входит ли текущий вид ВО в sign_codes новой причины
              const handbook40 = handbooks.find(h => h.id === 40);
              if (handbook40 && handbook40.references) {
                const currentVidVOItem = handbook40.references.find(r => r.id.toString() === currentVidVO);
                if (currentVidVOItem && currentVidVOItem.code) {
                  // Если code текущего вида ВО не входит в sign_codes новой причины - очищаем
                  if (!signCodes.includes(currentVidVOItem.code)) {
                    newFormData.ochagVidVO = '';
                  }
                }
              }
            } else if (currentVidVO && signCodes.length === 0) {
              // Если у причины нет sign_codes, очищаем вид ВО
              newFormData.ochagVidVO = '';
            }
          }
        }
      }
      
      updateFormData(newFormData);
    }
    
    closePicker();
  }, [pickerField, activeModalItemId, updateModalData, previousCause, loadSignsForCause, closePicker, pickerOptions, formData, updateFormData, handbooks]);

  const getFilteredOptions = useCallback(() => {
    let filtered = pickerOptions;
    
    if (pickerSearch.trim()) {
      const searchLower = pickerSearch.toLowerCase();
      filtered = pickerOptions.filter(option => 
        option.label.toLowerCase().includes(searchLower) ||
        (option.description && option.description.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [pickerOptions, pickerSearch]);

  const getSelectedIndexInFiltered = useCallback(() => {
    const filtered = getFilteredOptions();
    if (selectedOptionIndex >= 0 && selectedOptionIndex < pickerOptions.length) {
      const selectedOption = pickerOptions[selectedOptionIndex];
      return filtered.findIndex(opt => opt.value === selectedOption.value);
    }
    return -1;
  }, [getFilteredOptions, selectedOptionIndex, pickerOptions]);

  const openPickerWithOptions = useCallback((fieldName, options, itemId, currentValue = '') => {
    if (options && options.length > 0) {
      if (itemId) {
        setActiveModalItemId(itemId);
      } else {
        setActiveModalItemId(null);
      }
      
      const selectedIndex = options.findIndex(opt => opt.value === currentValue);
      
      setPickerField(fieldName);
      setPickerOptions(options);
      setPickerSearch('');
      setSelectedOptionIndex(selectedIndex);
      setShowPicker(true);
    }
  }, []);

  return {
    showPicker,
    pickerField,
    pickerOptions,
    pickerSearch,
    selectedOptionIndex,
    setSelectedOptionIndex,
    activeModalItemId,
    openPicker,
    openPickerWithOptions,
    closePicker,
    selectPickerValue,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    setPickerSearch
  };
};

